/**
 * Manual ordering for the day's item lists.
 *
 * Dragging a card to a new position used to write the reordered array and
 * nothing else — and nothing else is exactly what arrived. `mergeItems`
 * (dayWrites.ts) merges a day's items BY IDENTITY: it starts from the stored
 * array and applies per-item field changes, which is what stops two people
 * editing the same day from clobbering each other. A pure reorder changes no
 * item's fields, so every id compared equal, every one was skipped, and the
 * stored order came back untouched. The card picked up, moved, and landed
 * exactly where it started.
 *
 * So position has to BE a field. `order` already existed on the note type,
 * unused; these two functions are what make it real, for notes and for every
 * shortlist list.
 */

/**
 * Stamp a sequence onto the items, so the new order is a field change the
 * merge can carry.
 *
 * Every item in the list, not just the one that moved: a single moved item's
 * `order` means nothing without the others to sit between, and stamping the
 * lot also gives a list that has never been ordered a complete set on its
 * first drag.
 */
export function withOrder<T extends { order?: number }>(items: T[]): T[] {
  return items.map((item, index) => ({ ...item, order: index }));
}

/**
 * Sort by that stamp, leaving anything unstamped where it already sits.
 *
 * An unordered item falls back to its current index, and ties break on index
 * too, so a list that has never been dragged renders in exactly the order it
 * arrived and a half-stamped list doesn't scramble.
 */
export function byOrder<T extends { order?: number }>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index, key: item.order ?? index }))
    .sort((a, b) => a.key - b.key || a.index - b.index)
    .map(entry => entry.item);
}
